<template>
  <div class="programs-section">
    <!-- Toggle Section (only show when both options are available) -->
    <div v-if="showToggle" class="toggle-section">
      <div class="toggle-container">
        <!-- Sliding Background Indicator -->
        <div class="toggle-background-slider" :class="{ 'slide-right': activeTab === 'certificates' }"></div>

        <button
          v-if="isPathwaysAllowed"
          class="toggle-button"
          :class="{ active: activeTab === 'pathways' }"
          @click="activeTab = 'pathways'"
        >
          <img src="@/assets/icons/pathways-icon.svg" alt="Pathways" class="toggle-icon" />
          <span class="toggle-label">Pathways</span>
        </button>
        <button
          v-if="isCertificatesAvailable"
          class="toggle-button"
          :class="{ active: activeTab === 'certificates' }"
          @click="activeTab = 'certificates'"
        >
          <img src="@/assets/icons/certificates-icon.svg" alt="Certificates" class="toggle-icon" />
          <span class="toggle-label">Certificates</span>
        </button>
      </div>
    </div>

    <!-- Content Area -->
    <div class="pa-0" :class="{ 'no-toggle': !showToggle }">
      <!-- Pathways Content -->
      <div v-if="isPathwaysAllowed && activeTab === 'pathways'" class="content-section">
        <div class="section-header-clickable" @click="togglePathwaysCollapse">
          <div class="d-flex align-center">
            <h3 class="section-heading">Degree Pathways</h3>
            <span class="ml-1" style="display: inline-flex; vertical-align: middle" @click.stop>
              <InfoTooltip>
                Pathways is a tool designed to help you visualize your path into ASU. As always, you are encouraged to
                speak with your coach to discuss your transfer credits, course availability, admission requirements, and
                program costs.
              </InfoTooltip>
            </span>
          </div>
          <v-icon size="28px" color="#747474">
            {{ pathwaysCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
          </v-icon>
        </div>
        <p class="section-description">Select a degree you are interested in and find out what courses to take.</p>

        <!-- Show divider only when there's content below:
             1. If enrolled (header always shows)
             2. If not enrolled AND expanded -->
        <v-divider v-if="isEnrolledInPathway || !pathwaysCollapsed" class="section-divider my-4" />

        <!-- Enrolled pathway: Always show header, conditionally show courses -->
        <template v-if="isEnrolledInPathway">
          <enrolled-pathway-card :show-header-only="pathwaysCollapsed" @pathway-unenrolled="handlePathwayChange" />
        </template>

        <!-- Not enrolled: Show recommendations only when expanded -->
        <template v-else>
          <transition name="expand-collapse">
            <div v-show="!pathwaysCollapsed">
              <pathway-recommendation-card @pathway-enrolled="handlePathwayChange" />
            </div>
          </transition>
        </template>
      </div>

      <!-- Certificates Content -->
      <div v-if="isCertificatesAvailable && activeTab === 'certificates'" class="content-section">
        <div class="section-header-clickable" @click="toggleCertificatesCollapse">
          <h3 class="section-heading">Certificates</h3>
          <v-icon size="28px" color="#747474">
            {{ certificatesCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
          </v-icon>
        </div>
        <p class="section-description">
          Certificates are a great way to build job-ready skills and earn a credential you can add to your resume.
        </p>

        <!-- Enrolled certificates: Always show header, conditionally show courses -->
        <template v-if="certificates.length > 0">
          <v-divider class="section-divider my-4" />
          <template v-for="(cert, index) in certificates" :key="cert.connection_id">
            <enrolled-certificate-card
              :certificate="cert"
              :show-header-only="certificatesCollapsed"
              @unregister="handleUnregister"
              @reload="$emit('reload-certificates')"
              @reloadDashboardOrPathway="$emit('reloadDashboardOrPathway', $event)"
            />
            <v-divider v-if="index < certificates.length - 1" class="section-divider my-6" />
          </template>

          <!-- All enrolled certificates are completed: prompt to explore more -->
          <div v-if="allCertificatesCompleted" class="explore-more-certs mt-6">
            <div class="explore-more-text">
              <h5 class="explore-more-title">You're not currently registered for a certificate</h5>
              <p class="explore-more-description">Explore available certificates to find one that fits your goals.</p>
            </div>
            <UiButton variant="maroon" size="large" class="explore-more-btn" @click="openCertificatesPage">
              Explore certificates
            </UiButton>
          </div>
        </template>

        <!-- Not enrolled: Show empty state only when expanded -->
        <transition v-else name="expand-collapse">
          <div v-show="!certificatesCollapsed">
            <v-divider class="section-divider my-4" />
            <div class="empty-state-certs">
              <h5 class="empty-state-title">You're not registered for any certificates yet.</h5>
              <p class="empty-state-description">Explore available certificates to find one that fits your goals.</p>
              <UiButton variant="maroon" size="large" class="mt-4 explore-certs-btn" @click="openCertificatesPage">
                Explore certificates
              </UiButton>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import UiButton from './ui/Button.vue';
import EnrolledPathwayCard from './pathways/EnrolledPathwayCard.vue';
import PathwayRecommendationCard from './pathways/PathwayRecommendationCard.vue';
import EnrolledCertificateCard from './certificates/EnrolledCertificateCard.vue';
import InfoTooltip from '@/components/ui/InfoTooltip.vue';
import { Helper } from '@/mixins/helpers';
import { isCertificateEarned } from '@/constants/certificates';

export default defineComponent({
  name: 'PathwaysCertificatesSection',

  components: {
    UiButton,
    EnrolledPathwayCard,
    PathwayRecommendationCard,
    EnrolledCertificateCard,
    InfoTooltip
  },

  props: {
    certificates: {
      type: Array,
      default: () => []
    },
    unregisteringCertId: {
      type: [String, Number, null],
      default: null
    },
    hasPathway: {
      type: Boolean,
      default: false
    },
    isPathwaysAllowed: {
      type: Boolean,
      default: false
    },
    isCertificatesAvailable: {
      type: Boolean,
      default: false
    },
    isEnrolledInPathway: {
      type: Boolean,
      default: false
    },
    stayOnCertificatesTab: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    showToggle() {
      // Only show toggle if BOTH pathways and certificates are available
      return this.isPathwaysAllowed && this.isCertificatesAvailable;
    },
    allCertificatesCompleted() {
      // True when the learner has certificates and every one of them is earned.
      return this.certificates.length > 0 && this.certificates.every(isCertificateEarned);
    },
    defaultTab() {
      // After an in-session registration, keep the learner on the Certificates tab
      if (this.stayOnCertificatesTab && this.isCertificatesAvailable) return 'certificates';

      // Determine which tab to show:
      // - If both available: prefer pathways if enrolled, otherwise certificates if enrolled
      // - If only pathways available: show pathways
      // - If only certificates available: show certificates
      if (this.showToggle) {
        // Both available - check what user has
        if (this.hasPathway) return 'pathways';
        if (this.certificates.length > 0) return 'certificates';
        return 'pathways'; // Default to pathways if neither
      } else {
        // Only one available - show whichever is available
        return this.isPathwaysAllowed ? 'pathways' : 'certificates';
      }
    }
  },

  emits: ['unregister', 'pathway-enrolled', 'pathway-unenrolled', 'reload-certificates', 'reloadDashboardOrPathway'],

  created() {
    // Initialize activeTab after computed properties are ready
    this.activeTab = this.defaultTab;
  },

  data() {
    return {
      activeTab: null, // Initialize as null, will be set in created()
      pathwaysCollapsed: localStorage.getItem('pathwaysCollapsed') === 'true',
      certificatesCollapsed: localStorage.getItem('certificatesCollapsed') === 'true'
    };
  },

  watch: {
    pathwaysCollapsed(newValue) {
      localStorage.setItem('pathwaysCollapsed', newValue.toString());
    },

    certificatesCollapsed(newValue) {
      localStorage.setItem('certificatesCollapsed', newValue.toString());
    },

    // Update activeTab when defaultTab changes (e.g., when props load async)
    defaultTab(newTab) {
      this.activeTab = newTab;
    }
  },

  methods: {
    togglePathwaysCollapse() {
      this.pathwaysCollapsed = !this.pathwaysCollapsed;
    },

    toggleCertificatesCollapse() {
      this.certificatesCollapsed = !this.certificatesCollapsed;
    },

    handleUnregister(certificate) {
      this.$emit('unregister', certificate);
    },

    openCertificatesPage() {
      const baseUrl = Helper.methods.isUlcPartner()
        ? 'https://ulc.asu.edu/explore-courses/certificates/'
        : 'https://ea.asu.edu/courses/certificates/';
      window.open(baseUrl, '_blank');
    },

    handlePathwayChange() {
      // Emit the event up to the parent
      if (this.isEnrolledInPathway) {
        this.$emit('pathway-unenrolled');
      } else {
        this.$emit('pathway-enrolled');
      }
    }
  }
});
</script>

<style lang="stylus" scoped>
/* Programs Section Styles */
.programs-section {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: visible;
  margin-bottom: 24px;
  background-color: #ffffff;
}

/* Toggle Section */
.toggle-section {
  padding-bottom: 16px;
  background-color: #ffffff;
}

.toggle-container {
  display: flex;
  gap: 4px;
  padding: 4px;
  background-color: #E8E8E8;
  border-radius: 100px;
  width: 100%;
  position: relative;
}

/* Sliding Background Indicator */
.toggle-background-slider {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 8px);
  height: calc(100% - 8px);
  background-color: #FFFFFF;
  border-radius: 100px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.12);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
}

.toggle-background-slider.slide-right {
  transform: translateX(calc(100% + 8px));
}

.toggle-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0px 24px;
  border: none;
  border-radius: 100px;
  background-color: transparent;
  cursor: pointer;
  transition: color 0.3s ease;
  font-family: Arial, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #484848;
  height: 32px;
  position: relative;
  z-index: 2;
}

.toggle-button.active {
  color: #191919;
}

.toggle-button:hover:not(.active) {
  color: #191919;
}

.toggle-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.toggle-label {
  white-space: nowrap;
}

.content-section {
  width: 100%;
}

/* Section Header with Collapse */
.section-header-clickable {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
}

.section-heading {
  font-family: Arial, sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.03em;
  color: #191919;
  margin-bottom: 0;
  text-decoration: underline solid;
  text-underline-offset: -100%;
  text-decoration-thickness: 120%;
}

/* Divider styling */
.section-divider {
  border-color: #BFBFBF !important;
  opacity: 1 !important;
}

.section-description {
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #6B6B6B;
  margin-top: 8px;
  margin-bottom: 0;
}

/* Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.empty-state p {
  font-family: Arial, sans-serif;
  font-size: 16px;
  color: #6B6B6B;
}

/* Certificates Empty State */
.empty-state-certs {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 10px;
}

.empty-state-title {
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.03em;
  color: #191919;
  margin-bottom: 8px;
  text-decoration: underline solid;
  text-underline-offset: -100%;
  text-decoration-thickness: 150%;
}

.empty-state-description {
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0%;
  color: #6B6B6B;
  margin-bottom: 0;
}

/* Explore More Certificates (shown when all enrolled certificates are completed) */
.explore-more-certs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.explore-more-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.explore-more-title {
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.03em;
  color: #191919;
  margin-bottom: 0;
}

.explore-more-description {
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #6B6B6B;
  margin-bottom: 0;
}

.explore-more-btn {
  flex-shrink: 0;
}

/* Expand Collapse Animation */
.expand-collapse-enter-active,
.expand-collapse-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.expand-collapse-enter-from,
.expand-collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Responsive adjustments */
/* Tablet adjustments */
@media (max-width: 960px) {
  .toggle-section {
    padding: 16px;
  }

  .toggle-button {
    font-size: 15px;
    padding: 0px 20px;
    gap: 6px;
  }
}

/* Mobile adjustments - Better touch targets */
@media (max-width: 600px) {
  .toggle-section {
    padding: 12px 16px;
  }

  .toggle-container {
    padding: 3px;
    gap: 3px;
  }

  .toggle-background-slider {
    top: 3px;
    left: 3px;
    width: calc(50% - 4.5px);
    height: calc(100% - 6px);
  }

  .toggle-background-slider.slide-right {
    transform: translateX(calc(100% + 3px));
  }

  .toggle-button {
    font-size: 14px;
    padding: 0px 12px;
    height: 34px;
    gap: 6px;
  }

  .toggle-icon {
    width: 18px;
    height: 18px;
  }

  .toggle-label {
    font-size: 14px;
  }

  .explore-certs-btn {
    width: 100%;
  }

  .explore-more-certs {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .explore-more-text {
    gap: 4px;
  }

  .explore-more-btn {
    width: 100%;
    margin-top: 4px;
  }

  .explore-more-title {
    font-size: 15px;
    line-height: 22px;
  }

  .explore-more-description {
    font-size: 14px;
    line-height: 20px;
  }

  .section-heading {
    font-size: 20px;
    line-height: 24px;
    margin-top: 0;
  }

  .empty-state-certs {
    padding: 24px 16px;
  }

  .empty-state-title {
    font-size: 14px;
    line-height: 20px;
  }

  .empty-state-description {
    font-size: 14px;
    line-height: 20px;
  }
}

/* Extra small mobile devices */
@media (max-width: 400px) {
  .toggle-section {
    padding: 12px;
  }

  .toggle-button {
    font-size: 13px;
    padding: 0px 8px;
    gap: 4px;
  }

  .toggle-icon {
    width: 16px;
    height: 16px;
  }

  .toggle-label {
    font-size: 13px;
  }
}
</style>
